import { useEffect, useLayoutEffect, useState } from 'react';
import { Anchor, Box, Flex, ScrollArea, Stack, Tabs, Text, Title } from '@mantine/core';
import { useRouterState } from '@tanstack/react-router';
import { HELP_DOC_ORDER, HELP_DOCS, PARSED_DOCS } from './content';
import { Markdown } from './markdown';
import { QuickReference } from './QuickReference';

function readWindowHash(): string {
  return decodeURIComponent(window.location.hash.replace(/^#/, ''));
}

/** TOC entries, straight off content.ts's module-scope PARSED_DOCS — no
 * markdown parsing happens in this component. h2s (chapters) and h3s (their
 * subsections) keep the sidebar skimmable; deeper headings still get real
 * rendered ids for deep-linking, they're just not listed. */
const TOC = HELP_DOC_ORDER.map((docId) => ({
  doc: HELP_DOCS[docId],
  headings: PARSED_DOCS[docId].headings.filter((h) => h.depth >= 2 && h.depth <= 3),
}));

/** Full help centre (/help route, every role — see the route file's comment
 * for why it's deliberately not role-gated): a "Full guides" tab (all three
 * guides rendered in full, with a left-hand table of contents grouped by
 * guide, and hash deep-linking) plus a "Quick reference" tab (the printable
 * per-role cards — see QuickReference.tsx). `keepMounted={false}` on Tabs
 * keeps the inactive tab's content out of the DOM entirely rather than just
 * visually hidden — the quick-reference cards never mount (and the guides'
 * markdown parse never happens twice) until a user actually clicks over.
 *
 * A URL like /help#4a-purchasing scrolls that heading into view on load and
 * on any later hash change. Because keepMounted={false} unmounts the guides
 * panel while the quick-reference tab is showing, a guide deep link that
 * arrives THEN (a drawer "Open in full guide →", a pasted /help#slug, browser
 * back/forward) would otherwise have no mounted heading to scroll to — the
 * user would be left on the quick-reference tab and nothing would move. So the
 * Tabs value is CONTROLLED and any guide-targeting hash first FORCES the guides
 * tab active, with the scroll deferred to a layout effect that runs after that
 * switch has mounted the panel. An empty hash or a plain tab click never forces
 * the switch — quick-reference cards are never hash targets. Two navigation
 * channels feed the hash: TOC entries are native `#slug` anchors (the browser
 * scrolls and fires `hashchange` itself), while HelpDrawer's "Open in full
 * guide →" links navigate via the router (history.pushState — which does NOT
 * fire `hashchange`), so this component watches the router's hash too.
 *
 * Responsiveness: the guides tab's nav+content pair uses Mantine's
 * responsive style props (a `direction` object on `Flex`, a `w`/`mah` object
 * on the nav column) rather than a fixed-width `wrap="nowrap"` row, so on a
 * phone-width viewport the table of contents stacks above the guide content
 * instead of forcing horizontal scroll — see HelpCentre.test.tsx's
 * responsiveness test. */
export function HelpCentre() {
  const routerHash = useRouterState({ select: (s) => s.location.hash });
  const [currentHash, setCurrentHash] = useState(readWindowHash);
  // Controlled tab value (not defaultValue): a guide-targeting hash must be
  // able to FORCE the guides tab active even after the user has clicked over
  // to quick-reference, so the scroll below has a mounted guides panel to find
  // its target heading in — keepMounted={false} unmounts that panel otherwise.
  const [activeTab, setActiveTab] = useState<string | null>('guides');

  // Native TOC anchor clicks bypass the router (the browser scrolls itself and
  // fires `hashchange`): track the hash for aria-current, and — for a hash that
  // arrives while quick-reference is showing (browser back/forward, a pasted
  // /help#slug) — force the guides tab active so it still lands on the section.
  useEffect(() => {
    const onHashChange = () => {
      const id = readWindowHash();
      setCurrentHash(id);
      if (id) setActiveTab('guides');
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Router-driven hash changes: initial mount with a deep link, and drawer
  // "Open in full guide →" links clicked while already on /help — these use
  // history.pushState, which fires no `hashchange`. Record the hash and, for a
  // guide target, force the guides tab active; the scroll itself is deferred to
  // the layout effect below so it runs AFTER the switch has mounted the panel.
  useEffect(() => {
    const id = readWindowHash();
    setCurrentHash(id);
    if (id) setActiveTab('guides');
  }, [routerHash]);

  // Scroll the deep-link target into view once the guides panel is mounted.
  // Keyed on [activeTab, currentHash] and gated on the guides tab: when a hash
  // arrives while quick-reference is showing, the effects above flip activeTab
  // to 'guides', React mounts the guides panel in that commit, and this layout
  // effect — running after the DOM mutation, before paint — then finds the
  // freshly-mounted heading and scrolls to it. With no hash, or while another
  // tab is active, it no-ops, so quick-reference cards (never hash targets)
  // never trigger a scroll or a forced tab switch.
  useLayoutEffect(() => {
    if (activeTab !== 'guides' || !currentHash) return;
    document.getElementById(currentHash)?.scrollIntoView({ block: 'start' });
  }, [activeTab, currentHash]);

  return (
    <Stack gap="md">
      <Stack gap={2}>
        {/* a11y fix (session 10 nit): must be document order's first heading — this
            page's own h1, not an h2 with no h1 above it — so a guide's own leading
            `# ...` (rendered as a real h1 by markdown.tsx) never becomes the
            outline's first top-level heading. `size="h2"` decouples the visual size
            (unchanged) from `order` (the semantic/rendered tag). */}
        <Title order={1} size="h2">
          Help Centre
        </Title>
        <Text c="dimmed" size="sm" maw={640}>
          Every guide for every role, plus printable quick-reference cards — or open the Help button on
          any screen for just that screen's section.
        </Text>
      </Stack>
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="guides">Full guides</Tabs.Tab>
          <Tabs.Tab value="quick-reference">Quick reference</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="guides">
          <Flex align="flex-start" gap="xl" direction={{ base: 'column', sm: 'row' }}>
            <Box
              component="nav"
              aria-label="Help contents"
              w={{ base: '100%', sm: 260 }}
              style={{
                flexShrink: 0,
                position: 'sticky',
                // Offset below the AppShell's fixed header (Shell.tsx, height 64)
                // rather than sticking underneath it.
                top: 'calc(var(--app-shell-header-height, 0px) + var(--mantine-spacing-md))',
              }}
            >
              <ScrollArea.Autosize
                mah={{ base: 220, sm: 'calc(100vh - var(--app-shell-header-height, 0px) - 40px)' }}
              >
                <Stack gap="lg">
                  {TOC.map(({ doc, headings }) => (
                    <Stack key={doc.id} gap={4}>
                      <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                        {doc.title}
                      </Text>
                      <Stack gap={2}>
                        {headings.map((h) => {
                          const active = h.slug === currentHash;
                          return (
                            <Anchor
                              key={h.slug}
                              href={`#${h.slug}`}
                              size="sm"
                              c={active ? undefined : h.depth === 3 ? 'dimmed' : undefined}
                              // Bold weight marks the active entry as well as
                              // colour, and aria-current names it for AT. The
                              // background pill (current-section state, sidebar
                              // parity with Shell.tsx's NavLink) uses the same
                              // horizontal inset on every entry so activating
                              // one never shifts its text left/right.
                              fw={active ? 600 : undefined}
                              aria-current={active ? 'location' : undefined}
                              style={{
                                display: 'block',
                                marginLeft: -8,
                                paddingLeft: h.depth === 3 ? 'calc(var(--mantine-spacing-md) + 8px)' : 8,
                                paddingRight: 8,
                                borderRadius: 'var(--mantine-radius-sm)',
                                backgroundColor: active ? 'var(--mantine-color-graphite-0)' : undefined,
                              }}
                            >
                              {h.text}
                            </Anchor>
                          );
                        })}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Box>
            <Stack gap="xl" style={{ flex: 1, minWidth: 0, width: '100%' }}>
              {HELP_DOC_ORDER.map((docId) => {
                const doc = HELP_DOCS[docId];
                // Each guide opens with its own h1 ("Zebra Stock — User Guide"),
                // so no extra wrapper title — that kept rendering a duplicate,
                // competing heading right above the real one.
                return (
                  <Box key={doc.id}>
                    <Markdown content={doc.content} headings={PARSED_DOCS[docId].headings} />
                  </Box>
                );
              })}
            </Stack>
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="quick-reference">
          <QuickReference />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
